import React, { useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import Radio, { RadioProps } from '@mui/material/Radio';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup, { RadioGroupProps } from '@mui/material/RadioGroup';
import IconChecked from '../../assets/images/icons/question-design/checked.svg';
import { colors } from 'common/constants';
import { Box, styled, SxProps, Typography } from '@mui/material';
import BaseTextField from './base-text-field';
import { common } from '@mui/material/colors';

type Props = RadioGroupProps & {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- options can be any
    options: { label: string; value: any; image?: string; type?: number }[];
    label?: string;
    spacing?: number;
    helperText?: React.ReactNode;
    sxRadio?: SxProps;
    disabled?: boolean;
};

const BpIcon = styled('span')(({ theme }) => ({
    borderRadius: '50%',
    width: 30,
    height: 30,
    boxShadow:
        'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
    backgroundColor: '#f5f8fa',
    border: '1px solid black',
    [theme.breakpoints.up('xs')]: {
        width: 18,
        height: 18,
    },
    [theme.breakpoints.up('sm')]: {
        width: 18,
        height: 18,
    },
    [theme.breakpoints.up('md')]: {
        width: 20,
        height: 20,
    },
    [theme.breakpoints.up('xl')]: {
        width: 22,
        height: 22,
    },
    '.Mui-focusVisible &': {
        outline: '2px auto rgba(19,124,189,.6)',
        outlineOffset: 2,
    },
    'input:hover ~ &': {
        backgroundColor: '#ebf1f5',
        ...theme.applyStyles('dark', {
            backgroundColor: '#30404d',
        }),
    },
    'input:disabled ~ &': {
        boxShadow: 'none',
        background: 'rgba(206,217,224,.5)',
        ...theme.applyStyles('dark', {
            background: 'rgba(57,75,89,.5)',
        }),
    },
    ...theme.applyStyles('dark', {
        boxShadow: '0 0 0 1px rgb(16 22 26 / 40%)',
        backgroundColor: '#394b59',
        backgroundImage:
            'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))',
    }),
}));

const BpCheckedIcon = styled(BpIcon)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    '&::before': {
        display: 'block',
        backgroundImage: `url(${IconChecked})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '80% 80%',
        content: '""',
        width: '100%',
        height: '100%',
    },
    'input:checked ~ &': { border: 'none' },
    'input:hover ~ &': {
        backgroundColor: theme.palette.primary.main,
    },
}));

export function BpRadio(props: RadioProps) {
    return (
        <Radio
            disableRipple
            color="default"
            checkedIcon={<BpCheckedIcon />}
            icon={<BpIcon />}
            {...props}
        />
    );
}

export default function BaseRadioGroup({
    row,
    name,
    label,
    options,
    spacing,
    helperText,
    sxRadio,
    disabled,
    ...other
}: Props) {
    const { control, getValues, setValue, watch } = useFormContext();
    const otherTextComponentName = `${name}OtherText`;
    const answerValue = watch(name);
    const optionType1 = options.find((option) => option.type === 1);
    const [showOtherTextBox, setShowOtherTextBox] = React.useState(
        Number(getValues(name)) === optionType1?.value
    );

    useEffect(() => {
        if (Number(answerValue) !== optionType1?.value) {
            setValue(otherTextComponentName, '');
        }
        setShowOtherTextBox(Number(answerValue) === optionType1?.value);
    }, [answerValue, optionType1?.value, otherTextComponentName, setValue]);

    const labelledby = label ? `${name}-${label}` : '';

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <FormControl
                    component="fieldset"
                    sx={{
                        width: '100%',
                    }}
                >
                    {label && (
                        <FormLabel
                            component="legend"
                            id={labelledby}
                            sx={{ typography: 'body2' }}
                        >
                            {label}
                        </FormLabel>
                    )}

                    <RadioGroup
                        {...field}
                        aria-labelledby={labelledby}
                        row={row}
                        {...other}
                    >
                        {options.map((option) => (
                            <Box key={option.value}>
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={
                                        <BpRadio
                                            sx={{ ...sxRadio }}
                                            onClick={(
                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                e: any
                                            ) => {
                                                if (
                                                    e.target.value ===
                                                    field.value
                                                ) {
                                                    field.onChange(null);
                                                }
                                            }}
                                            disabled={disabled}
                                        />
                                    }
                                    label={option.label}
                                    sx={{
                                        ...(row && {
                                            mr: 0,
                                            '&:not(:last-of-type)': {
                                                mr: spacing || 2,
                                            },
                                        }),
                                        ...sxRadio,
                                    }}
                                />
                                {option.type === 1 && showOtherTextBox && (
                                    <BaseTextField
                                        name={otherTextComponentName}
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 255,
                                            },
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                backgroundColor: common.white,
                                                borderRadius: '10px',
                                            },
                                        }}
                                    />
                                )}
                            </Box>
                        ))}
                    </RadioGroup>

                    {(!!error || helperText) && (
                        <FormHelperText
                            error={!!error}
                            sx={{
                                fontSize: '14px',
                                marginLeft: 0,
                                color: `${colors.textError} !important`,
                                fontWeight: '400',
                            }}
                        >
                            {error ? error?.message : helperText}
                        </FormHelperText>
                    )}
                </FormControl>
            )}
        />
    );
}

export function BaseRadioGroupWithImage({
    name,
    label,
    options,
    helperText,
}: Props) {
    const { control, getValues, setValue, watch } = useFormContext();
    const otherTextComponentName = `${name}OtherText`;
    const answerValue = watch(name);
    const optionType1 = options.find((option) => option.type === 1);
    const [showOtherTextBox, setShowOtherTextBox] = React.useState(
        Number(getValues(name)) === optionType1?.value
    );

    useEffect(() => {
        if (Number(answerValue) !== optionType1?.value) {
            setValue(otherTextComponentName, '');
        }
        setShowOtherTextBox(Number(answerValue) === optionType1?.value);
    }, [answerValue, optionType1?.value, otherTextComponentName, setValue]);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                    {label && (
                        <FormLabel
                            component="legend"
                            sx={{ typography: 'body2' }}
                        >
                            {label}
                        </FormLabel>
                    )}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: [
                                'repeat(2, 1fr)',
                                'repeat(2, 1fr)',
                                'repeat(3, 1fr)',
                            ],
                            alignItems: 'stretch',
                            gap: [3, 5],
                            width: '100%',
                        }}
                    >
                        {options.map((option, index) => {
                            const selected =
                                field.value != null &&
                                String(field.value) === String(option.value);
                            return (
                                <Box
                                    key={index}
                                    onClick={() =>
                                        field.onChange(
                                            selected ? null : option.value
                                        )
                                    }
                                    sx={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: option.image
                                            ? 'flex-start'
                                            : 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    {option.image && (
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                                aspectRatio: '4 / 3',
                                                overflow: 'hidden',
                                                backgroundColor: '#F3F4F6',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={option.image}
                                                alt={option.label}
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                }}
                                            />
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <BpRadio
                                            checked={selected}
                                            readOnly
                                            tabIndex={-1}
                                            sx={{
                                                p: 0,
                                                pointerEvents: 'none',
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: [
                                                    '0.7rem',
                                                    '1rem',
                                                    '1rem',
                                                    '1.6rem',
                                                    '1.8rem',
                                                ],
                                                fontWeight: 500,
                                                color: selected
                                                    ? colors.main
                                                    : [
                                                          common.white,
                                                          common.black,
                                                      ],
                                                lineHeight: 1.35,
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            {option.label}
                                        </Typography>
                                    </Box>
                                    {option.type === 1 && showOtherTextBox && (
                                        <Box
                                            sx={{ pt: 0.5 }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <BaseTextField
                                                name={otherTextComponentName}
                                                InputProps={{
                                                    inputProps: {
                                                        maxLength: 255,
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiInputBase-root': {
                                                        backgroundColor:
                                                            common.white,
                                                        borderRadius: '10px',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {(!!error || helperText) && (
                        <FormHelperText
                            error={!!error}
                            sx={{
                                fontSize: '14px',
                                marginLeft: 0,
                                color: `${colors.textError} !important`,
                                fontWeight: '400',
                            }}
                        >
                            {error ? error?.message : helperText}
                        </FormHelperText>
                    )}
                </FormControl>
            )}
        />
    );
}
